import type { EggStyle, Topping, WaffleKind } from '../game/types';
import { px, spriteFromDraw, spriteFromGrid, type Palette, type Sprite } from './sprite';

/* 16×16 pixel icons for the kitchen buttons. */

export const ICON_PALETTE: Palette = {
  k: '#2a1a10',
  w: '#fffaf0',
  W: '#cfc6b8',
  y: '#ffd23f',
  Y: '#e8a93a',
  o: '#f08a24',
  b: '#9a6232',
  B: '#5e3a1c',
  r: '#e5433b',
  R: '#a8281f',
  p: '#f4a3a8',
  P: '#d9707a',
  g: '#58b947',
  G: '#2f7d32',
  l: '#f3e0b0',
  t: '#caa274',
  T: '#8f6a45',
  u: '#8e4fa8',
  U: '#dcb8ea',
  c: '#f5ead2',
  C: '#d8c7a4',
  s: '#b8c0c8',
  S: '#6d7680',
  x: '#3a2a20',
  h: '#e7b75a',
  H: '#b97a2c',
  e: '#ffb300',
  m: '#6b3a1f',
  n: '#7a4a24',
};

const GRIDS: Record<string, readonly string[]> = {
  hashBrowns: [
    '................',
    '................',
    '................',
    '.....hHh.hH.....',
    '...hHhhHhhHhh...',
    '..hhHhHhhHhhHh..',
    '.hHhhHhHhHhHhhh.',
    '.hhHhhhHhhhHhHh.',
    'WwHhHhHhhHhHhhwW',
    'wwwhhHhhHhhHhwww',
    'Wwwwwwwwwwwwwwww',
    '.WwwwwwwwwwwwwW.',
    '..WWWWWWWWWWWW..',
    '................',
    '................',
    '................',
  ],
  smothered: [
    '................',
    '.......G........',
    '......GG........',
    '.......G........',
    '.....kkkkk......',
    '....kUUuUUk.....',
    '...kUuUUUuUk....',
    '..kUuUwwwUuUk...',
    '..kUuwUUUwuUk...',
    '..kUuwUwUwuUk...',
    '..kUuwUUUwuUk...',
    '..kUuUwwwUuUk...',
    '...kUuUUUuUk....',
    '....kkUUUkk.....',
    '......kkk.......',
    '................',
  ],
  covered: [
    '................',
    '................',
    '................',
    '...........kk...',
    '.........kkyk...',
    '.......kkyyyk...',
    '.....kkyyoyyk...',
    '...kkyyyyyyyk...',
    '..kyyyyyyoyyk...',
    '..kYYYYYYYYYk...',
    '..kYoYYYYYoYk...',
    '..kYYYYYYYYYk...',
    '..kYYYYoYYYYk...',
    '..kkkkkkkkkkk...',
    '................',
    '................',
  ],
  chunked: [
    '................',
    '................',
    '....kkkkkkkk....',
    '..kkppppppppkk..',
    '.kpppPPpppppppk.',
    'kppPppppppPPpppk',
    'kpppppwwwpppPppk',
    'kpPpppwllwppppPk',
    'kppppwlllwpPpppk',
    'kpPppwwwwppppppk',
    '.kppppppppPpppk.',
    '..kkppppppppkk..',
    '....kkkkkkkk....',
    '................',
    '................',
    '................',
  ],
  diced: [
    '................',
    '................',
    '......G.G.......',
    '.....GGgGG......',
    '....kkGGGkk.....',
    '...krrrgrrrk....',
    '..krrwrrrrrRk...',
    '..krwrrrrrrRk...',
    '..krrrrrrrrRk...',
    '..krrrrrrrrRk...',
    '..krrrrrrrRRk...',
    '...krrrrrRRk....',
    '....kkRRRkk.....',
    '......kkk.......',
    '................',
    '................',
  ],
  peppered: [
    '................',
    '...........G....',
    '..........GG....',
    '.........kGk....',
    '........kgggk...',
    '.......kgwggGk..',
    '......kgwggGk...',
    '.....kggggGk....',
    '....kgggGGk.....',
    '...kgggGGk......',
    '..kgggGGk.......',
    '..kggGGk........',
    '..kGGGk.........',
    '...kkk..........',
    '................',
    '................',
  ],
  capped: [
    '................',
    '................',
    '.....kkkkkk.....',
    '...kkttttttkk...',
    '..kttwtttttttk..',
    '.kttwwttttttTTk.',
    '.ktttttttttTTTk.',
    '.kTTTTTTTTTTTTk.',
    '..kkkkllllkkkk..',
    '.....kllllk.....',
    '.....klllTk.....',
    '.....klllTk.....',
    '.....kllTTk.....',
    '......kkkk......',
    '................',
    '................',
  ],
  topped: [
    '................',
    '.......W..W.....',
    '......W..W......',
    '.......W..W.....',
    '................',
    '...kkkkkkkkkk...',
    '..kRrRrRrRrRRk..',
    '.krrBrrRrrBrrRk.',
    '.kRrrrBrrrrRrRk.',
    '.kwwwwwwwwwwwWk.',
    '..kwwwwwwwwwWk..',
    '...kwwwwwwwWk...',
    '....kkkkkkkk....',
    '................',
    '................',
    '................',
  ],
  country: [
    '................',
    '................',
    '................',
    '..c.............',
    '.ccckkkkkkkk....',
    '..kcccxcccxckk..',
    '...kcccccxccck.k',
    '...kxcccccccckk.',
    '....kwwwwwwwwk..',
    '....kwwwwwwwWk..',
    '.....kwwwwwWk...',
    '......kWWWWk....',
    '.......kkkk.....',
    '................',
    '................',
    '................',
  ],
  scrambled: [
    '................',
    '................',
    '................',
    '................',
    '.....yyeyy......',
    '...yyeyyyeyy....',
    '..yeyyyeyyyyey..',
    '.yyyeyyyyeyyyyy.',
    'WwyyyyeyyyyeyywW',
    'wwwyeyyyyeyyywww',
    'Wwwwwwwwwwwwwwww',
    '.WwwwwwwwwwwwwW.',
    '..WWWWWWWWWWWW..',
    '................',
    '................',
    '................',
  ],
  overEasy: [
    '................',
    '................',
    '................',
    '.....wwwww......',
    '...wwwwwwwww....',
    '..wwwwwwwwwwww..',
    '.wwwwwkkkkwwwww.',
    '.wwwwkeeeekwwwc.',
    '.wwwwkeyeekwwcc.',
    '.wwwwkeeeekwwc..',
    '..wwwwkkkkwwwc..',
    '...wwwwwwwwcc...',
    '....cwwwwccc....',
    '......cccc......',
    '................',
    '................',
  ],
  bacon: [
    '................',
    '................',
    '.........kkk....',
    '.......kkrRrk...',
    '.....kkrwrRrk...',
    '...kkrRwwRrkk...',
    '..krrRwwRrrk....',
    '..kRrwwRrkk.kkk.',
    '...kkRrrk.kkrRrk',
    '....kkkkkkrwrRrk',
    '.......kkrRwwRkk',
    '......krrRwwRrk.',
    '......kRrwwRrk..',
    '.......kkRrrk...',
    '.........kkk....',
    '................',
  ],
  coffee: [
    '................',
    '.....W..W.......',
    '....W..W........',
    '.....W..W.......',
    '................',
    '..kkkkkkkkk.....',
    '..kmmmmmmmkkkk..',
    '..kwwwwwwwkwwk..',
    '..kwwrwwwwk..k..',
    '..kwwwwwwwkwwk..',
    '..kwwwwwwwkkk...',
    '..kWwwwwwWk.....',
    '...kWWWWWk......',
    '....kkkkk.......',
    '................',
    '................',
  ],
};

function waffleIcon(kind: WaffleKind): Sprite {
  return spriteFromDraw(`icon:waffle:${kind}`, 16, 16, (ctx) => drawWaffle(ctx, 8, 8, 7, kind, 1));
}

/**
 * Round Waffle House-style waffle: golden disc with a square grid.
 * `done` 0 → pale batter, 1 → golden, >1 → darker.
 */
export function drawWaffle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  kind: WaffleKind,
  done: number,
): void {
  const light = done < 0.5 ? '#f6e3a8' : done > 1.2 ? '#6b3f1a' : '#f2b64c';
  const dark = done < 0.5 ? '#e4c67a' : done > 1.2 ? '#3d2410' : '#c9812b';
  const rim = done > 1.2 ? '#24140a' : '#8a5220';
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const d = x * x + y * y;
      if (d > r * r + r * 0.6) continue;
      const edge = d > (r - 1) * (r - 1) + r * 0.6;
      const grid = (x + r + 1) % 3 === 0 || (y + r + 1) % 3 === 0;
      px(ctx, cx + x, cy + y, 1, 1, edge ? rim : grid ? dark : light);
    }
  }
  if (kind === 'pecan') {
    const spots = [
      [-3, -2],
      [2, -3],
      [3, 2],
      [-2, 3],
      [0, 0],
    ] as const;
    for (const [x, y] of spots) {
      px(ctx, cx + x, cy + y, 2, 1, '#5e3216');
      px(ctx, cx + x, cy + y + 1, 2, 1, '#7a4a24');
    }
  }
}

export function toppingIcon(t: Topping): Sprite {
  return spriteFromGrid(`icon:${t}`, GRIDS[t] ?? [], ICON_PALETTE);
}

export function hashBrownIcon(): Sprite {
  return spriteFromGrid('icon:hashBrowns', GRIDS.hashBrowns ?? [], ICON_PALETTE);
}

export function eggIcon(style: EggStyle): Sprite {
  return spriteFromGrid(`icon:${style}`, GRIDS[style] ?? [], ICON_PALETTE);
}

export function baconIcon(): Sprite {
  return spriteFromGrid('icon:bacon', GRIDS.bacon ?? [], ICON_PALETTE);
}

export function coffeeIcon(): Sprite {
  return spriteFromGrid('icon:coffee', GRIDS.coffee ?? [], ICON_PALETTE);
}

export function waffleKindIcon(kind: WaffleKind): Sprite {
  return waffleIcon(kind);
}
